import { Component, EventEmitter, Input, OnInit, Output, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LocalizePipe } from '@shared/common/pipes/localize.pipe';

interface JsonNode {
    key: string;
    value?: any;
    type: 'object' | 'array' | 'string' | 'number' | 'boolean' | 'null';
    level: number;
    isExpanded?: boolean;
    changeType?: 'added' | 'removed' | 'modified' | 'unchanged';
    originalValue?: any;
    newValue?: any;
    hasChildren?: boolean;
    parentPath?: string;
    fullPath?: string;
    children?: JsonNode[];
    arrayIndex?: number;
    // Selection state for the per-line "import this change?" checkbox.
    // Only set when selectionEnabled is true. true/false on changed leaves; tri-state
    // on parents is computed at render time from descendant state.
    selected?: boolean;
}

export interface JsonSelectionChange {
    /** Merged JSON computed from current selection: source values for selected paths, dest values otherwise. */
    mergedJson: string;
    /** True when the user has unchecked at least one changed line (= partial selection). */
    hasUnselectedChanges: boolean;
    /** True when nothing is selected — caller can treat as "skip this row". */
    nothingSelected: boolean;
}

@Component({
    selector: 'ez-json-comparison',
    templateUrl: './ez-json-comparison.component.html',
    styleUrls: ['./ez-json-comparison.component.css'],
    imports: [CommonModule, LocalizePipe],
    standalone: true
})
export class EzJsonComparisonComponent implements OnInit, OnChanges {
    @Input() originalJson = '';
    @Input() newJson = '';
    @Input() showComparison = true;
    /** When true, render a checkbox on each changed node so the caller can pick which lines to import. */
    @Input() selectionEnabled = false;

    /** Emitted whenever the user toggles a per-line checkbox. Caller uses this to build its import payload. */
    @Output() selectionChanged = new EventEmitter<JsonSelectionChange>();

    /**
     * When true, render a small copy button on each changed leaf node so the caller can
     * copy the original (left/older) value back into the current document.
     * Defaults to false — existing read-only callers are unaffected.
     */
    @Input() copyable = false;

    /** Emitted when the user clicks the copy button on a changed leaf node. */
    @Output() copyField = new EventEmitter<{ path: string; value: any }>();

    originalTree: JsonNode[] = [];
    newTree: JsonNode[] = [];
    comparisonTree: JsonNode[] = [];
    displayMode: 'all' | 'changed' = 'changed';
    showRawJson = false;

    // Track when both JSONs exist but are identical (no differences)
    isIdentical = false;

    // Store the hierarchical tree structures for toggle functionality
    private hierarchicalComparisonTree: JsonNode[] = [];
    private hierarchicalNewTree: JsonNode[] = [];

    ngOnInit(): void {
        this.parseAndCompare();
    }

    ngOnChanges(): void {
        this.parseAndCompare();
    }

    parseAndCompare(): void {
        // Reset identical flag
        this.isIdentical = false;

        if (this.originalJson) {
            this.originalTree = this.flattenTree(this.parseJson(this.originalJson));
        }

        if (this.newJson) {
            this.hierarchicalNewTree = this.parseJson(this.newJson);
            this.newTree = this.flattenTree(this.hierarchicalNewTree);
        }

        if (this.showComparison && (this.originalJson || this.newJson)) {
            if (this.originalJson && this.newJson) {
                // Both values exist - do comparison
                const comparedTrees = this.compareJsonTrees(this.parseJson(this.originalJson), this.parseJson(this.newJson));

                // Check if all nodes are unchanged (identical JSONs)
                this.isIdentical = this.areAllNodesUnchanged(comparedTrees);

                const filteredTrees = this.displayMode === 'all' ?
                    this.getAllElementsWithValues(comparedTrees) :
                    this.getChangedElementsOnly(comparedTrees);
                this.hierarchicalComparisonTree = filteredTrees;
                this.comparisonTree = this.flattenTree(filteredTrees);
            } else {
                // Only one value exists - treat as added or removed
                const jsonValue = this.newJson || this.originalJson;
                const changeType = this.newJson ? 'added' : 'removed';
                const parsedTree = this.parseJson(jsonValue);

                // Mark all nodes with the appropriate change type
                this.markAllNodes(parsedTree, changeType);
                this.hierarchicalComparisonTree = parsedTree;
                this.comparisonTree = this.flattenTree(parsedTree);
            }

            // Initialize per-line selection state on every changed node so the user starts
            // with "import everything that's different" — same as today's row-level checkbox.
            this.initializeSelection(this.hierarchicalComparisonTree);
        }
    }

    parseJson(jsonString: string): JsonNode[] {
        if (!jsonString || jsonString.trim() === '') {
            return [];
        }

        const originalString = jsonString; // Keep for error reporting
        let processedString = jsonString.trim();

        try {
            // Step 1: Try direct parse first (fastest path)
            try {
                const parsed = JSON.parse(processedString);
                return this.parseJsonValue('root', parsed, 0);
            } catch (directError) {
                // Continue to escape handling
            }

            // Step 2: Handle escaped JSON strings (double serialization)
            // Pattern: "{\"key\":\"value\"}" -> {"key":"value"}
            let unescapeAttempts = 0;
            const maxAttempts = 3; // Prevent infinite loops

            while (unescapeAttempts < maxAttempts) {
                // Check if wrapped in quotes with escaped content
                if (processedString.startsWith('"') && processedString.endsWith('"')) {
                    try {
                        const unquoted = JSON.parse(processedString);
                        if (typeof unquoted === 'string') {
                            processedString = unquoted;
                            unescapeAttempts++;
                            continue; // Try parsing again
                        } else {
                            // Successfully parsed to object/array
                            return this.parseJsonValue('root', unquoted, 0);
                        }
                    } catch (e) {
                        break; // Can't unquote, stop trying
                    }
                }
                break; // No more unescaping needed
            }

            // Step 3: Try parsing after unescaping
            const parsed = JSON.parse(processedString);
            return this.parseJsonValue('root', parsed, 0);

        } catch (error) {
            // Step 4: Show informative error with data preview
            const preview = originalString.length > 100
                ? originalString.substring(0, 100) + '...'
                : originalString;

            console.error('JSON parse error:', error);
            console.error('Failed to parse:', preview);

            return [{
                key: 'Parse Error',
                value: `Invalid JSON: ${error}`,
                type: 'string',
                level: 0,
                changeType: 'unchanged'
            }];
        }
    }

    private parseJsonValue(key: string, value: any, level: number, arrayIndex?: number): JsonNode[] {
        const result: JsonNode[] = [];

        const node: JsonNode = {
            key: arrayIndex !== undefined ? `[${arrayIndex}]` : key,
            level: level,
            isExpanded: level < 2, // Expand first 2 levels by default
            changeType: 'unchanged',
            type: this.getJsonType(value),
            arrayIndex: arrayIndex
        };

        // Opt-in: keys ending in "Json" (e.g. UiConfigJson, EntityDataJson) hold serialized JSON.
        // Parse the inner JSON so it renders as an expandable subtree instead of one giant string.
        let effectiveValue = value;
        if (typeof value === 'string' && this.shouldExpandJsonString(key, arrayIndex)) {
            const parsed = this.tryParseJsonString(value);
            if (parsed !== undefined) {
                effectiveValue = parsed;
            }
        }

        if (effectiveValue === null) {
            node.value = null;
            node.type = 'null';
        } else if (Array.isArray(effectiveValue)) {
            node.type = 'array';
            node.children = [];
            effectiveValue.forEach((item, index) => {
                const childNodes = this.parseJsonValue(key, item, level + 1, index);
                node.children!.push(...childNodes);
            });
            node.hasChildren = node.children.length > 0;
            node.value = `Array[${effectiveValue.length}]`;
        } else if (typeof effectiveValue === 'object') {
            node.type = 'object';
            node.children = [];
            Object.keys(effectiveValue).forEach(objKey => {
                const childNodes = this.parseJsonValue(objKey, effectiveValue[objKey], level + 1);
                node.children!.push(...childNodes);
            });
            node.hasChildren = node.children.length > 0;
            node.value = `Object{${Object.keys(effectiveValue).length}}`;
        } else {
            node.value = effectiveValue;
        }

        result.push(node);
        return result;
    }

    private shouldExpandJsonString(key: string, arrayIndex?: number): boolean {
        // Only opt in for object properties — array element keys are `[N]`, not domain names.
        if (arrayIndex !== undefined) {
return false;
}
        return !!key && /Json$/.test(key);
    }

    private tryParseJsonString(value: string): any {
        if (!value) {
return undefined;
}
        const trimmed = value.trim();
        if (trimmed.length < 2) {
return undefined;
}

        const open = trimmed[0];
        const close = trimmed[trimmed.length - 1];
        const isObject = open === '{' && close === '}';
        const isArray = open === '[' && close === ']';
        if (!isObject && !isArray) {
return undefined;
}

        // Peek past whitespace to the first content char — JSON content can only start
        // with these, so bracketed prose like "[Custom CSS exists]" never reaches JSON.parse.
        let i = 1;
        while (i < trimmed.length - 1 && /\s/.test(trimmed[i])) {
i++;
}
        const c = trimmed[i];
        const contentLooksLikeJson =
            c === close ||
            c === '"' || c === '{' || c === '[' ||
            c === '-' || (c >= '0' && c <= '9') ||
            c === 't' || c === 'f' || c === 'n';
        if (!contentLooksLikeJson) {
return undefined;
}

        try {
            const parsed = JSON.parse(trimmed);
            if (parsed !== null && typeof parsed === 'object') {
                return parsed;
            }
        } catch {
            // Not valid JSON — fall back to showing the raw string
        }
        return undefined;
    }

    private getJsonType(value: any): 'object' | 'array' | 'string' | 'number' | 'boolean' | 'null' {
        if (value === null) {
return 'null';
}
        if (Array.isArray(value)) {
return 'array';
}
        if (typeof value === 'object') {
return 'object';
}
        if (typeof value === 'string') {
return 'string';
}
        if (typeof value === 'number') {
return 'number';
}
        if (typeof value === 'boolean') {
return 'boolean';
}
        return 'string';
    }

    private markAllNodes(nodes: JsonNode[], changeType: 'added' | 'removed'): void {
        nodes.forEach(node => {
            node.changeType = changeType;
            if (node.children) {
                this.markAllNodes(node.children, changeType);
            }
        });
    }

    private areAllNodesUnchanged(nodes: JsonNode[]): boolean {
        for (const node of nodes) {
            if (node.changeType !== 'unchanged') {
                return false;
            }
            if (node.children && node.children.length > 0) {
                if (!this.areAllNodesUnchanged(node.children)) {
                    return false;
                }
            }
        }
        return true;
    }

    compareJsonTrees(originalNodes: JsonNode[], newNodes: JsonNode[]): JsonNode[] {
        return this.compareNodeArrays(originalNodes, newNodes, 0);
    }

    private compareNodeArrays(originalNodes: JsonNode[], newNodes: JsonNode[], level: number): JsonNode[] {
        // Set-like scalar arrays (e.g. a Role's GrantedPermissions = list of permission-name
        // strings) must be matched by VALUE, not array index. With index matching, one extra or
        // missing element offsets the entire list so every downstream row reads "modified".
        // Matching by value yields the true added/removed set — for permissions the key is the
        // permission name itself.
        if (this.isScalarArrayContext(originalNodes, newNodes)) {
            return this.compareScalarArraysByValue(originalNodes, newNodes, level);
        }

        const result: JsonNode[] = [];

        // Create maps by key for easier comparison
        const originalMap = new Map<string, JsonNode>();
        const newMap = new Map<string, JsonNode>();

        // Group original nodes by key
        originalNodes.forEach(node => {
            const key = node.arrayIndex !== undefined ? `[${node.arrayIndex}]` : node.key;
            originalMap.set(key, node);
        });

        // Group new nodes by key
        newNodes.forEach(node => {
            const key = node.arrayIndex !== undefined ? `[${node.arrayIndex}]` : node.key;
            newMap.set(key, node);
        });

        // Get all unique keys
        const allKeys = new Set([...originalMap.keys(), ...newMap.keys()]);

        // Compare each key
        allKeys.forEach(key => {
            // Per-tenant volatile surrogates (e.g. configId) must never drive a diff — skip them
            // entirely so they neither show as "modified" nor mark their parent modified.
            if (this.isVolatileComparisonKey(key)) {
                return;
            }
            const originalNode = originalMap.get(key);
            const newNode = newMap.get(key);

            if (originalNode && newNode) {
                // Element exists in both - compare
                const comparedNode = this.compareSingleNode(originalNode, newNode, level);
                result.push(comparedNode);
            } else if (originalNode && !newNode) {
                // Element removed — mark the WHOLE subtree 'removed', not just the top node.
                // cloneNode leaves descendants 'unchanged', so the changed-only filter would
                // strip them and collapse the node to a detail-less "[n] Removed" leaf.
                const removedNode = this.cloneNode(originalNode, level);
                this.markAllNodes([removedNode], 'removed');
                result.push(removedNode);
            } else if (!originalNode && newNode) {
                // Element added — mark the WHOLE subtree 'added' (see note above) so the
                // changed-only view shows what was added instead of a bare "[n] Added" row.
                const addedNode = this.cloneNode(newNode, level);
                this.markAllNodes([addedNode], 'added');
                result.push(addedNode);
            }
        });

        return result;
    }

    /**
     * True when BOTH sides are arrays of scalar leaf elements (string/number/boolean/null) and at
     * least one side is non-empty. Such arrays are treated as unordered sets and matched by value.
     * An empty side is compatible (an empty array can pair with a scalar array). Arrays of objects
     * fall through to the default index/key-based matching.
     */
    private isScalarArrayContext(originalNodes: JsonNode[], newNodes: JsonNode[]): boolean {
        const compatible = this.allScalarArrayElements(originalNodes) && this.allScalarArrayElements(newNodes);
        return compatible && (originalNodes.length > 0 || newNodes.length > 0);
    }

    private allScalarArrayElements(nodes: JsonNode[]): boolean {
        return nodes.every(n =>
            n.arrayIndex !== undefined &&
            (!n.children || n.children.length === 0) &&
            (n.type === 'string' || n.type === 'number' || n.type === 'boolean' || n.type === 'null')
        );
    }

    /**
     * Compare two scalar arrays as sets, keyed by element value. Source-order common+removed items
     * first (unchanged if also in dest, else removed), then dest-only items appended as added.
     * The parent array node is still marked modified by compareSingleNode when any child changed.
     */
    private compareScalarArraysByValue(originalNodes: JsonNode[], newNodes: JsonNode[], level: number): JsonNode[] {
        const result: JsonNode[] = [];
        const valueKey = (n: JsonNode) => this.normalizeValue(n.value);

        const originalValues = new Set(originalNodes.map(valueKey));
        const newValues = new Set(newNodes.map(valueKey));

        originalNodes.forEach(origNode => {
            const node = this.cloneNode(origNode, level);
            node.changeType = newValues.has(valueKey(origNode)) ? 'unchanged' : 'removed';
            result.push(node);
        });

        newNodes.forEach(newNode => {
            if (!originalValues.has(valueKey(newNode))) {
                const node = this.cloneNode(newNode, level);
                node.changeType = 'added';
                result.push(node);
            }
        });

        return result;
    }

    private compareSingleNode(originalNode: JsonNode, newNode: JsonNode, level: number): JsonNode {
        const result: JsonNode = {
            key: newNode.key,
            type: newNode.type,
            level: level,
            isExpanded: level < 2,
            hasChildren: false,
            changeType: 'unchanged',
            arrayIndex: newNode.arrayIndex
        };

        // Compare values
        const originalValue = this.normalizeValue(originalNode.value);
        const newValue = this.normalizeValue(newNode.value);

        if (originalValue !== newValue) {
            if (originalNode.value === undefined && newNode.value !== undefined) {
                result.changeType = 'added';
                result.value = newNode.value;
            } else if (originalNode.value !== undefined && newNode.value === undefined) {
                result.changeType = 'removed';
                result.value = originalNode.value;
            } else {
                result.changeType = 'modified';
                result.originalValue = originalNode.value;
                result.newValue = newNode.value;
                result.value = newNode.value;
            }
        } else {
            result.value = newNode.value;
        }

        // Compare children recursively
        const originalChildren = originalNode.children || [];
        const newChildren = newNode.children || [];

        if (originalChildren.length > 0 || newChildren.length > 0) {
            result.children = this.compareNodeArrays(originalChildren, newChildren, level + 1);
            result.hasChildren = result.children.length > 0;

            // If any children changed, mark parent as modified
            const hasChangedChildren = result.children.some(child =>
                child.changeType === 'added' ||
                child.changeType === 'removed' ||
                child.changeType === 'modified'
            );

            if (hasChangedChildren && result.changeType === 'unchanged') {
                result.changeType = 'modified';
                result.isExpanded = true; // Auto-expand to show changes
            }
        }

        return result;
    }

    private cloneNode(node: JsonNode, level: number): JsonNode {
        const cloned: JsonNode = {
            key: node.key,
            value: node.value,
            type: node.type,
            level: level,
            isExpanded: level < 2,
            hasChildren: false,
            changeType: 'unchanged',
            arrayIndex: node.arrayIndex
        };

        if (node.children && node.children.length > 0) {
            cloned.children = node.children.map(child => this.cloneNode(child, level + 1));
            cloned.hasChildren = true;
        }

        return cloned;
    }

    private normalizeValue(value: any): string {
        if (value === null || value === undefined) {
            return '';
        }
        // A leaf string may hold serialized JSON (e.g. a widget's `config` = {"configId":"<guid>"}).
        // Strip per-tenant volatile surrogate keys before comparing so functionally-identical
        // payloads don't read as "modified" purely because of a regenerated GUID.
        if (typeof value === 'string') {
            const stripped = this.stripVolatileKeysFromJsonString(value);
            if (stripped !== undefined) {
                return stripped;
            }
        }
        return JSON.stringify(value);
    }

    /**
     * Per-tenant volatile surrogate keys that must never drive a diff. `configId` is a random
     * GUID minted per tenant when a dashboard widget config is created — it's a reference, not a
     * value, and is copied verbatim on import. Mirrors the backend TenantConfigurationCompareService.
     */
    private static readonly VOLATILE_COMPARISON_KEYS = ['configId'];

    private isVolatileComparisonKey(key: string): boolean {
        return EzJsonComparisonComponent.VOLATILE_COMPARISON_KEYS.includes(key);
    }

    /**
     * If `value` is a string holding a JSON object, return it re-serialized with volatile keys
     * removed (recursively). Returns undefined when the string isn't a JSON object, so callers
     * fall back to the default normalization.
     */
    private stripVolatileKeysFromJsonString(value: string): string | undefined {
        const trimmed = value.trim();
        if (trimmed.length < 2 || trimmed[0] !== '{' || trimmed[trimmed.length - 1] !== '}') {
            return undefined;
        }
        let parsed: any;
        try {
            parsed = JSON.parse(trimmed);
        } catch {
            return undefined;
        }
        if (parsed === null || typeof parsed !== 'object') {
            return undefined;
        }
        this.deleteVolatileKeys(parsed);
        return JSON.stringify(parsed);
    }

    private deleteVolatileKeys(obj: any): void {
        if (Array.isArray(obj)) {
            obj.forEach(item => this.deleteVolatileKeys(item));
            return;
        }
        if (obj !== null && typeof obj === 'object') {
            for (const key of Object.keys(obj)) {
                if (this.isVolatileComparisonKey(key)) {
                    delete obj[key];
                } else {
                    this.deleteVolatileKeys(obj[key]);
                }
            }
        }
    }

    toggleNode(node: JsonNode): void {
        const targetTree = this.showComparison && this.hierarchicalComparisonTree.length > 0 ?
            this.hierarchicalComparisonTree : this.hierarchicalNewTree;

        this.findAndToggleNodeInTree(targetTree, node.fullPath || node.key, !node.isExpanded);
        this.refreshTree();
    }

    private findAndToggleNodeInTree(tree: JsonNode[], targetPath: string, newExpandedState: boolean, currentPath: string = ''): boolean {
        for (const node of tree) {
            const nodePath = currentPath ? `${currentPath}/${node.key}` : node.key;

            if (nodePath === targetPath) {
                node.isExpanded = newExpandedState;
                return true;
            }

            if (node.children && this.findAndToggleNodeInTree(node.children, targetPath, newExpandedState, nodePath)) {
                return true;
            }
        }
        return false;
    }

    showAll(): void {
        this.displayMode = 'all';
        // Must re-parse to apply filter, not just refresh
        this.parseAndCompare();
    }

    showChangedOnly(): void {
        this.displayMode = 'changed';
        // Must re-parse to apply filter, not just refresh
        this.parseAndCompare();
    }

    toggleRaw(): void {
        this.showRawJson = !this.showRawJson;
    }

    expandAll(): void {
        // Must modify hierarchical tree, not flattened tree
        const targetTree = this.showComparison && this.hierarchicalComparisonTree.length > 0
            ? this.hierarchicalComparisonTree
            : this.hierarchicalNewTree;
        this.setAllExpanded(targetTree, true);
        this.refreshTree();
    }

    collapseAll(): void {
        // Must modify hierarchical tree, not flattened tree
        const targetTree = this.showComparison && this.hierarchicalComparisonTree.length > 0
            ? this.hierarchicalComparisonTree
            : this.hierarchicalNewTree;
        this.setAllExpanded(targetTree, false);
        this.refreshTree();
    }

    private setAllExpanded(nodes: JsonNode[], expanded: boolean): void {
        nodes.forEach(node => {
            node.isExpanded = expanded;
            if (node.children) {
                this.setAllExpanded(node.children, expanded);
            }
        });
    }

    getIndentation(level: number): string {
        return '  '.repeat(level);
    }

    getOriginalValueForDisplay(node: JsonNode): string {
        if (node.changeType === 'added') {
            return '';
        }
        if (node.originalValue !== undefined) {
            return this.formatValue(node.originalValue);
        }
        return this.formatValue(node.value);
    }

    getNewValueForDisplay(node: JsonNode): string {
        if (node.changeType === 'removed') {
            return '';
        }
        if (node.newValue !== undefined) {
            return this.formatValue(node.newValue);
        }
        return this.formatValue(node.value);
    }

    formatValue(value: any): string {
        if (value === null) {
return 'null';
}
        if (value === undefined) {
return '';
}
        if (typeof value === 'string') {
return `"${value}"`;
}
        return String(value);
    }

    getTypeIcon(type: string): string {
        switch (type) {
            case 'object': return 'fa-cube';
            case 'array': return 'fa-list';
            case 'string': return 'fa-quote-right';
            case 'number': return 'fa-hashtag';
            case 'boolean': return 'fa-check-square';
            case 'null': return 'fa-circle-o';
            default: return 'fa-question';
        }
    }

    flattenTree(nodes: JsonNode[]): JsonNode[] {
        const result: JsonNode[] = [];

        const flatten = (nodeList: JsonNode[], parentPath: string = '', parentExpanded: boolean = true, level: number = 0) => {
            nodeList.forEach((node) => {
                // Skip hidden properties
                if (this.shouldHideProperty(node.key)) {
                    return;
                }

                const currentPath = parentPath ? `${parentPath}/${node.key}` : node.key;

                // Set additional properties
                node.fullPath = currentPath;
                node.parentPath = parentPath;
                node.level = level;
                node.hasChildren = node.children && node.children.length > 0;

                // Only add the node if its parent is expanded (or it's a root node)
                if (parentExpanded) {
                    result.push(node);
                }

                // Add children if they exist and the current node is expanded
                if (node.children && node.children.length > 0 && node.isExpanded && parentExpanded) {
                    flatten(node.children, currentPath, true, level + 1);
                }
            });
        };

        flatten(nodes);
        return result;
    }

    private getAllElementsWithValues(comparedNodes: JsonNode[]): JsonNode[] {
        return this.filterNodesByMode(comparedNodes, 'all');
    }

    private getChangedElementsOnly(comparedNodes: JsonNode[]): JsonNode[] {
        return this.filterNodesByMode(comparedNodes, 'changed');
    }

    private filterNodesByMode(nodes: JsonNode[], mode: 'all' | 'changed'): JsonNode[] {
        const result: JsonNode[] = [];

        for (const node of nodes) {
            // Skip idGuid properties
            if (this.shouldHideProperty(node.key)) {
                continue;
            }

            let shouldInclude = false;
            let filteredNode: JsonNode | null = null;

            if (mode === 'all') {
                shouldInclude = true;
                filteredNode = { ...node };
            } else {
                // In 'changed' mode, include nodes that are added, removed, or modified
                shouldInclude = node.changeType === 'added' ||
                               node.changeType === 'removed' ||
                               node.changeType === 'modified';
                if (shouldInclude) {
                    filteredNode = { ...node };
                }
            }

            // Process children recursively
            if (node.children && node.children.length > 0) {
                const filteredChildren = this.filterNodesByMode(node.children, mode);

                if (mode === 'changed') {
                    // If any children are changed, include this parent node
                    if (filteredChildren.length > 0) {
                        shouldInclude = true;
                        if (!filteredNode) {
                            filteredNode = { ...node };
                            // Mark parent as modified if it has changed children but was originally unchanged
                            if (filteredNode.changeType === 'unchanged') {
                                filteredNode.changeType = 'modified';
                            }
                            // Auto-expand to show changed children
                            filteredNode.isExpanded = true;
                        }
                    } else {
                        // If no changed children and parent was only included due to children, exclude it
                        if (filteredNode && filteredNode.changeType === 'unchanged') {
                            shouldInclude = false;
                            filteredNode = null;
                        }
                    }
                }

                if (filteredNode) {
                    if (filteredChildren.length > 0) {
                        filteredNode.children = filteredChildren;
                        filteredNode.hasChildren = true;
                        // Ensure parent is expanded if it has changed children
                        if (mode === 'changed') {
                            filteredNode.isExpanded = true;
                        }
                    } else {
                        filteredNode.children = [];
                        filteredNode.hasChildren = false;

                        // In 'changed' mode, if parent has no changed children and was unchanged, don't include it
                        if (mode === 'changed' && node.changeType === 'unchanged') {
                            shouldInclude = false;
                            filteredNode = null;
                        }
                    }
                }
            }

            if (shouldInclude && filteredNode) {
                result.push(filteredNode);
            }
        }

        return result;
    }

    private shouldHideProperty(propertyName: string): boolean {
        // List of properties to hide from the comparison
        const hiddenProperties = [
            'idGuid',
            'id',  // Also hide regular id if needed
            'guidInstanceId',
            // Add other properties to hide here
        ];

        return hiddenProperties.includes(propertyName);
    }

    refreshTree(): void {
        // In comparison mode with any JSON, always use comparison tree
        if (this.showComparison && (this.originalJson || this.newJson)) {
            this.comparisonTree = this.flattenTree(this.hierarchicalComparisonTree);
        } else if (this.newJson) {
            // Single JSON view without comparison mode
            this.newTree = this.flattenTree(this.hierarchicalNewTree);
        }
    }

    // ──────────────────────────────────────────────────────────────────────
    // Per-line selection
    // ──────────────────────────────────────────────────────────────────────

    /**
     * Mark every changed node as selected by default. Called after parseAndCompare
     * rebuilds the tree so the user starts with "import everything that's different",
     * matching the existing row-level checkbox behavior.
     */
    private initializeSelection(nodes: JsonNode[]): void {
        for (const node of nodes) {
            if (node.changeType !== 'unchanged') {
                node.selected = true;
            }
            if (node.children && node.children.length > 0) {
                this.initializeSelection(node.children);
            }
        }
    }

    /**
     * Tri-state for a parent node, computed at render time from its descendants:
     *  - 'all'  → every changed descendant is selected (checkbox checked)
     *  - 'none' → every changed descendant is unselected (checkbox empty)
     *  - 'some' → mixed (checkbox indeterminate)
     */
    getNodeSelectionState(node: JsonNode): 'all' | 'some' | 'none' {
        if (!node.children || node.children.length === 0) {
            return node.selected ? 'all' : 'none';
        }
        let sawSelected = false;
        let sawUnselected = false;
        const visit = (n: JsonNode) => {
            if (n.changeType !== 'unchanged') {
                if (n.selected) {
sawSelected = true;
} else {
sawUnselected = true;
}
            }
            if (n.children) {
n.children.forEach(visit);
}
        };
        node.children.forEach(visit);
        // Self counts too if it's a leaf change-marked node
        if (node.changeType !== 'unchanged' && (!node.children || node.children.length === 0)) {
            if (node.selected) {
sawSelected = true;
} else {
sawUnselected = true;
}
        }
        if (sawSelected && sawUnselected) {
return 'some';
}
        if (sawSelected) {
return 'all';
}
        return 'none';
    }

    /** True when this node should display a checkbox (it's a change or has changed descendants). */
    isSelectable(node: JsonNode): boolean {
        if (!this.selectionEnabled) {
return false;
}
        if (node.changeType !== 'unchanged') {
return true;
}
        return this.hasChangedDescendant(node);
    }

    private hasChangedDescendant(node: JsonNode): boolean {
        if (!node.children) {
return false;
}
        for (const child of node.children) {
            if (child.changeType !== 'unchanged') {
return true;
}
            if (this.hasChangedDescendant(child)) {
return true;
}
        }
        return false;
    }

    /**
     * Toggle handler for the per-line checkbox. The flat tree shares node references
     * with the hierarchical tree, so mutating `node` propagates to both views.
     * Cascades: every changed descendant takes the new value.
     */
    toggleSelection(node: JsonNode, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        const state = this.getNodeSelectionState(node);
        // Tri-state click: 'all' or 'some' → uncheck all; 'none' → check all
        const newSelected = state === 'none';
        this.cascadeSelection(node, newSelected);
        this.emitSelectionChanged();
    }

    private cascadeSelection(node: JsonNode, selected: boolean): void {
        if (node.changeType !== 'unchanged') {
            node.selected = selected;
        }
        if (node.children) {
            node.children.forEach(child => this.cascadeSelection(child, selected));
        }
    }

    private emitSelectionChanged(): void {
        if (!this.selectionEnabled) {
return;
}
        const change = this.computeSelectionChange();
        this.selectionChanged.emit(change);
    }

    private computeSelectionChange(): JsonSelectionChange {
        const sourceObj = this.parseRawJson(this.originalJson);
        const destObj = this.parseRawJson(this.newJson);

        // Walk hierarchicalComparisonTree and apply user's "leave dest's value" overrides
        // onto a clone of the source structure. Result is the JSON that should be imported.
        const merged = this.deepClone(sourceObj);
        const stats = { selected: 0, unselected: 0 };

        if (this.hierarchicalComparisonTree.length > 0) {
            const root = this.hierarchicalComparisonTree[0];
            // The compare tree has a synthetic 'root' node — its children are the actual top-level props
            if (root.children) {
                this.applyUnselectedOverrides(root.children, merged, destObj, stats);
            }
        }

        const mergedJson = merged === undefined
            ? this.originalJson
            : JSON.stringify(merged);

        return {
            mergedJson: mergedJson,
            hasUnselectedChanges: stats.unselected > 0,
            nothingSelected: stats.selected === 0 && stats.unselected > 0,
        };
    }

    /**
     * For each changed node where the user unchecked the line, replace the corresponding
     * path in `sourceParent` with the value from `destParent` (= leave dest's value
     * untouched). For 'removed' (path exists in source only), unchecking deletes the path.
     * For 'added' (path exists in dest only), unchecking re-introduces dest's value.
     */
    private applyUnselectedOverrides(
        nodes: JsonNode[],
        sourceParent: any,
        destParent: any,
        stats: { selected: number; unselected: number }
    ): void {
        for (const node of nodes) {
            const propKey: string | number = node.arrayIndex !== undefined ? node.arrayIndex : node.key;
            const sourceVal = sourceParent != null ? sourceParent[propKey] : undefined;
            const destVal = destParent != null ? destParent[propKey] : undefined;

            if (node.changeType !== 'unchanged') {
                if (node.selected === false) {
                    stats.unselected++;
                    if (sourceParent != null) {
                        if (node.changeType === 'removed') {
                            // source has it, dest doesn't — unselected = drop from merged
                            if (Array.isArray(sourceParent) && typeof propKey === 'number') {
                                sourceParent[propKey] = undefined;
                            } else {
                                delete sourceParent[propKey];
                            }
                        } else {
                            // 'modified' or 'added' — write dest's value (or remove key if dest lacks it)
                            if (destVal === undefined) {
                                if (Array.isArray(sourceParent) && typeof propKey === 'number') {
                                    sourceParent[propKey] = undefined;
                                } else {
                                    delete sourceParent[propKey];
                                }
                            } else {
                                sourceParent[propKey] = this.deepClone(destVal);
                            }
                        }
                    }
                    // Whole subtree is now dest's value; no need to recurse further.
                    continue;
                }
                stats.selected++;
            }
            if (node.children && node.children.length > 0 && sourceVal != null && destVal != null) {
                this.applyUnselectedOverrides(node.children, sourceVal, destVal, stats);
            }
        }
    }

    private parseRawJson(json: string): any {
        if (!json) {
return undefined;
}
        try {
            return JSON.parse(json);
        } catch {
            return undefined;
        }
    }

    private deepClone<T>(value: T): T {
        if (value === undefined || value === null) {
return value;
}
        return JSON.parse(JSON.stringify(value));
    }

    /**
     * Emits a copyField event so the caller can apply the original (left/older) value for
     * the given path back into the current document. Only called when copyable is true.
     */
    onCopyField(path: string, value: any): void {
        this.copyField.emit({ path, value });
    }
}
